// My Desk panel catalog — Request Ledger N-090 (owner, 2026-09-08):
//   1. "Cards can be set in different sizes ... both horizontal and
//      vertical — like a real canvas."
//   3. "Need to give them the ability to add various tables."
//
// One entry per panel TYPE this app can show on the canvas: label,
// description, which role may add/see it, its shipped default size, and the
// component that renders it. DeskGrid.tsx is the only consumer — it never
// imports a panel component directly, it looks the type up here.
//
// This file's role list is mirrored, BY HAND, on the server
// (src/desk.js PANEL_TYPES) so a saved layout can be validated without the
// server importing a .tsx tree — standing rule 12 (explicit registries over
// inference) accepts the duplication. Add a type here, add it there too.
//
// A `.tsx` file (not `.ts`, despite carrying no other markup) so `render()`
// can compose real components as plain JSX rather than raw `h()` calls —
// `h()`'s overload resolution rejects a component whose props are not
// exactly `{}` when called with an empty object, which is not a real type
// error (every one of these components' props are optional) but reads as
// one; JSX sidesteps it entirely.
import type { JSX } from 'preact';
import type { Role } from '../lib/nav';
import { session } from '../lib/session';
import { recruitingPreview } from '../lib/ownerPreview';
import { state, peekResource, resourcePendingFetch } from '../lib/store';
import { clinicToday } from '../lib/clinicDay';
import { visibleClinics } from '../components/ClinicTabs';
import type { LocationCode } from '../lib/locations';
import {
  SERVICE_COMPLIANCE_KEY, ALERT_DAYS, isDueSoon, type ServiceComplianceData,
} from '../lib/serviceCompliance';
import { licensesNeedingAttention, LICENSES_KEY, isExpiringSoon, type LicensesData } from '../lib/licenses';
import { leasesNeedingAttention, LEASES_KEY, type LeasesData } from '../lib/leases';
import { policiesNeedingAttention, INSURANCE_KEY, type InsuranceData } from '../lib/insurance';
import { RaiseAlerts, HOUR_RAISES_KEY, type HourRaisesData } from '../components/RaiseAlerts';
import {
  NeedsHireDetailsAlert, NEEDS_HIRE_DETAILS_KEY, canSeeNewHires, type NeedsHireDetailsData,
} from '../components/NeedsHireDetailsAlert';
import { ComplianceAlerts } from '../components/ComplianceAlerts';
import { BirthdayAlerts } from '../components/BirthdayAlerts';
import { CommunicationsPanel } from '../components/CommunicationsPanel';
import { MyMailPanel } from '../components/MyMailPanel';
import { MyCalendarPanel } from '../components/MyCalendarPanel';
import {
  RecruitingDeskPanel, recruitingScopeFor, recruitingBlockFor,
} from '../components/recruiting/RecruitingDeskPanel';
import { newApplicationsInScope } from '../components/recruiting/NewApplicationsSection';
import { COMMUNICATIONS_KEY, type CommunicationsData } from '../lib/communications';
import {
  RECRUITING_SUMMARY_KEY, RECRUITING_NEW_APPLICATIONS_KEY,
  type RecruitingSummary, type NewApplicationsResponse,
} from '../lib/recruitingApi';
import { Canvas } from '../components/Canvas';
import { DeskPeople } from '../components/DeskPeople';
import { QuickDocs } from '../components/QuickDocs';
import { ClinicOverviewStrip } from '../components/ClinicOverviewStrip';
import { NeedsAttentionPanel } from '../components/NeedsAttentionPanel';
import {
  PayHoursTableForClinicPanel, SpcListForClinicPanel, BonusTrackerForClinicPanel, DepositReconForClinicPanel,
} from './clinicBoardPanels';
import { resolveOverlaps, floatUp } from './gridPack';
import { withPermanentPanels } from './fixedBlock';

// --- "does this panel currently have anything to show" -------------------
//
// A grid cell that would otherwise be empty must leave no gap in VIEW mode
// (build spec item 1) — but `entry.render()` returns a preact VNODE
// (`<RaiseAlerts />`), which is just a description of "call this component
// function" — it does NOT run the component and cannot tell us whether that
// call will return null. Preact only finds that out during its own render
// pass, after this file has already decided whether to show the wrapper.
// So "has content" is answered SEPARATELY and synchronously here, reading
// the exact same store/resource data each component reads internally —
// deliberately duplicated (never imported from the component, which would
// require restructuring five components this file does not own) and kept
// beside each entry so drift is easy to spot in review.
function hasTasksOrWorkItems(): boolean {
  return Object.keys(state.value.standing ?? {}).length > 0
    || Object.keys(state.value.task ?? {}).length > 0
    || Object.keys(state.value.workItem ?? {}).length > 0;
}

/** Owner, 2026-09-10: NeedsAttentionPanel.tsx also lists licenses expired or
 * expiring within LICENSE_ATTENTION_DAYS for the viewer's clinics — this
 * peek mirrors that, same reason spcAlertsHasContent mirrors the license
 * merge below (the panel is only mounted once this says there is content).
 *
 * Owner, 2026-09-26: the panel also gained lease lines, OWNER/DIRECTOR only
 * (leasesRoleGate) — mirrored here the same way, so a manager's peek never
 * even reads LEASES_KEY. Insurance joined the same evening, same gate
 * (insuranceRoleGate), same reasoning — a manager's peek never reads
 * INSURANCE_KEY either. */
function needsAttentionHasContent(): boolean {
  if (hasTasksOrWorkItems()) return true;
  const licenseData = peekResource<LicensesData>(LICENSES_KEY);
  const clinics = new Set<string>(visibleClinics());
  if (licensesNeedingAttention(licenseData?.rows ?? [], clinics, clinicToday(new Date())).length > 0) return true;
  const me = session.value.me;
  if (me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR')) {
    const leaseData = peekResource<LeasesData>(LEASES_KEY);
    if (leasesNeedingAttention(leaseData?.leases ?? [], clinicToday(new Date())).length > 0) return true;
    const insuranceData = peekResource<InsuranceData>(INSURANCE_KEY);
    if (policiesNeedingAttention(insuranceData?.policies ?? [], clinicToday(new Date())).length > 0) return true;
  }
  // Owner, 2026-09-27: must-read communications join this same list, for
  // EVERY role (no gate — communicationsHasContent below reads the same
  // key the same way).
  const communicationsData = peekResource<CommunicationsData>(COMMUNICATIONS_KEY);
  if ((communicationsData?.communications ?? []).some((c) => c.mustRead && c.unread)) return true;
  return false;
}

function payRateAlertsHasContent(): boolean {
  const data = peekResource<HourRaisesData>(HOUR_RAISES_KEY);
  return Boolean(data?.raises?.length);
}

function newHiresHasContent(): boolean {
  if (!canSeeNewHires(session.value.me?.role)) return false;
  const data = peekResource<NeedsHireDetailsData>(NEEDS_HIRE_DETAILS_KEY);
  return Boolean(data?.people?.length);
}

/** Mirrors ComplianceAlerts.tsx's own filter exactly — role, visible
 * clinics, due-soon window.
 *
 * This reads whatever is ALREADY cached — it does not itself fetch, and
 * never has (the module comment above explains why: `entry.render()` alone
 * cannot tell DeskGrid whether the real component would come back empty).
 * The reason it used to go stale-and-silent after `service-compliance` had
 * rows again is not this synchronous peek — it is that bootstrap
 * (lib/bootstrap.ts) treated ANY cached answer, including a stale one
 * mirrored from a PREVIOUS session's IndexedDB copy, as "already handled,
 * skip the fetch" — so the fresh answer this peek needed was never asked
 * for. Fixed at that source (bootstrap's `keep*` flags, via
 * `resourcePendingFetch`), not here. */
function spcAlertsHasContent(): boolean {
  const role = session.value.me?.role;
  if (role !== 'OWNER' && role !== 'DIRECTOR' && role !== 'MANAGER') return false;
  const today = clinicToday(new Date());
  const clinics = new Set(visibleClinics());
  const data = peekResource<ServiceComplianceData>(SERVICE_COMPLIANCE_KEY);
  const rows = (data?.rows ?? []).filter((r) => clinics.has(r.locationCode as LocationCode) && isDueSoon(r, today));
  if (rows.length > 0) return true;
  // N-094: ComplianceAlerts.tsx now merges "License expiring" into this same
  // list (its own useResource(LICENSES_KEY) call) — this peek has to mirror
  // that merge or a license-only alert would be invisible: the real
  // component never gets mounted to show it, because DeskGrid only mounts
  // it once THIS check says there is something to show.
  const licenseData = peekResource<LicensesData>(LICENSES_KEY);
  const licenseRows = (licenseData?.rows ?? [])
    .filter((r) => clinics.has(r.locationCode as LocationCode) && isExpiringSoon(r, today));
  return licenseRows.length > 0;
}

/** The clinic-scoped panels only return null when the viewer has literally
 * no visible clinic at all — an edge case, but the same rule as
 * clinicBoardPanels.tsx's own resolveClinic (which falls back to the first
 * visible clinic, so "none visible" is the only way it comes up empty). */
function hasAnyVisibleClinic(): boolean {
  return visibleClinics().length > 0;
}

/** N-094 ("forward and file"): CommunicationsPanel fetches its own key via
 * `useResource` (same shape as ComplianceAlerts), so it needs a matching
 * synchronous peek here — see BirthdayAlerts.tsx's own comment on why a
 * `useResource`-backed panel must never go on a role's default layout
 * without one (it would fire an unstubbed fetch from every test that puts
 * this type on a canvas). No role gate: every mapped role sees this panel
 * (ALL_ROLES), same as the server scopes /api/v1/communications. */
function communicationsHasContent(): boolean {
  const data = peekResource<CommunicationsData>(COMMUNICATIONS_KEY);
  return Boolean(data?.communications?.some((c) => !c.deskRetired));
}

/** My Mail / My Calendar (owner, 2026-09-10 / "build it" 2026-09-18): unlike
 * every other `useResource`-backed panel above, this is never gated on
 * having anything to show — a PERSONAL panel the person chose to add
 * should show its own calm line ("Mail is not connected yet.", "Inbox is
 * empty.") rather than vanish behind the catalog's generic placeholder.
 * The one thing that DOES take it off the canvas is `ctx.viewingAs`: while
 * an owner/director is viewing the app as someone else, the server itself
 * refuses both endpoints (P2, `src/my_google.js`) — so this mirrors that
 * here too, rather than firing a fetch DeskGrid already knows will be
 * blocked. `emptyLabel` below carries the same "Hidden while viewing as"
 * line the panel component ALSO renders on its own (defense in depth: the
 * component is tested standalone too, and must never trust that this gate
 * ran first). */
function myGooglePanelHasContent(): boolean {
  return !session.value.me?.viewingAs;
}

/** Recruiting (owner ask, 2026-09-25): real content only in owner preview —
 * everyone else always sees ComingSoonPanel (`true` here, same shape as
 * myGooglePanelHasContent above; DeskGrid must never swap that placeholder
 * out for the generic "nothing to show" one).
 *
 * For the owner-preview case, `resourcePendingFetch` decides "don't know
 * yet" first, per store.ts's own doc comment on that function: a key this
 * panel has never fetched (no other surface primed it, e.g. My Desk loads
 * before anyone has opened People → Recruiting this session) must mount the
 * real panel so ITS OWN `useResource` can fetch — reading a cold cache as
 * "empty" is the exact bug that once hid the SPC panel behind "Nothing to
 * show right now" (see spcAlertsHasContent's own comment above). Only once
 * a real answer is in hand does this count stages/newToday/quiet7 to decide
 * whether there is anything worth showing. */
function recruitingHasContent(config: PanelConfig | undefined): boolean {
  if (!recruitingPreview()) return true;
  if (resourcePendingFetch(RECRUITING_SUMMARY_KEY)) return true;
  if (resourcePendingFetch(RECRUITING_NEW_APPLICATIONS_KEY)) return true;
  const data = peekResource<RecruitingSummary>(RECRUITING_SUMMARY_KEY);
  if (!data) return true;
  const visible = visibleClinics();
  const scope = recruitingScopeFor(config?.clinic, visible);
  // New Indeed applicants (owner, 2026-09-29/30) are part of this panel now.
  const apps = peekResource<NewApplicationsResponse>(RECRUITING_NEW_APPLICATIONS_KEY);
  if (apps && newApplicationsInScope(apps, scope, visible).length > 0) return true;
  const block = recruitingBlockFor(data, scope);
  return block.newToday > 0 || (block.stages.TO_REVIEW ?? 0) > 0 || block.quiet7 > 0 || (block.stages.OFFER ?? 0) > 0;
}

// --- sizing ------------------------------------------------------------
//
// N-090b (owner, 2026-09-08): "make boxes bigger horizontally and
// vertically" replaced the six-button Width/Height segmented control with
// dragging a panel's own edges (client/src/components/DeskGrid.tsx). The
// two closed sets of values that control used to expose as button labels
// are still exactly what the server accepts (src/desk.js WIDTHS / HEIGHTS)
// and what a drag snaps to on release — they just live as plain numbers in
// client/src/desk/gridPack.ts (SNAP_WIDTHS / SNAP_HEIGHTS) now, next to the
// snapping math, rather than as labelled buttons here.
export type PanelWidth = 3 | 4 | 6 | 8 | 9 | 12;
export type PanelHeight = 1 | 2 | 3 | 4;

// --- layout shape --------------------------------------------------------

export interface PanelConfig {
  clinic?: string;
}

export interface DeskPanelInstance {
  id: string;
  type: PanelType;
  w: PanelWidth;
  h: PanelHeight;
  col: number;
  row: number;
  config?: PanelConfig;
}

export interface DeskLayout {
  version: 1;
  panels: DeskPanelInstance[];
}

export type PanelType =
  | 'clinicStrip'
  | 'payRateAlerts'
  | 'newHires'
  | 'spcAlerts'
  | 'birthdays'
  | 'needsAttention'
  | 'canvas'
  | 'people'
  | 'quickDocs'
  | 'payHoursTable'
  | 'spcListForClinic'
  | 'bonusTrackerForClinic'
  | 'depositReconForClinic'
  | 'communications'
  | 'myMail'
  | 'myCalendar'
  | 'recruiting';

export interface PanelCatalogEntry {
  type: PanelType;
  label: string;
  /** Live title when it depends on who is looking (the clinic strip). */
  labelFor?: () => string;
  description: string;
  roles: Role[];
  defaultSize: { w: PanelWidth; h: PanelHeight };
  clinicConfigurable?: boolean;
  /** A clinic-configurable panel that can also show every clinic the viewer
   * can see summed (`config.clinic === 'all'`, which src/desk.js accepts on
   * any clinic-configurable type). DeskGrid.tsx offers "All clinics" in the
   * clinic picker only for these — an explicit flag (standing rule 12), not
   * a type-name check that goes stale when a type is renamed. */
  allClinics?: boolean;
  /** Owner, 2026-10-12: "these need to be on permanently: Needs attention,
   * Communications, SPC, nvRecruiter, My Mail." A permanent panel is always
   * on the Desk of every role that may see it — checked and disabled
   * ("Always on") in the Desk checklist, no remove button on the panel, and
   * src/desk.js PERMANENT_PANEL_TYPES adds it back to any layout missing
   * it. Since 2026-10-12 ("have them not move at all") the five also sit at
   * fixed spots and sizes under the clinic strip — fixedBlock.ts
   * PERMANENT_PANEL_LAYOUT — and cannot be moved or resized. */
  permanent?: boolean;
  /** N-090b: the four-clinic strip is the one panel whose WIDTH is not the
   * viewer's to change — it reports on every clinic the viewer can see, so
   * anything narrower would just crop the row of tiles. Height still moves
   * freely. DeskGrid.tsx reads this to skip rendering the right-edge and
   * corner resize grips (and the shift+left/right keyboard step) for this
   * type only — an explicit registry flag (standing rule 12), not a
   * `type === 'clinicStrip'` check buried in the grid component. */
  lockWidth?: boolean;
  /** N-090c (owner, 2026-09-08 — the four-clinic strip clipped its own
   * tiles at the shipped default height): the shortest height this TYPE may
   * ever be, in either the shipped default layout (defaultLayouts.ts) or a
   * drag/keyboard resize (DeskGrid.tsx) — below `defaultSize.h` when unset.
   * An explicit registry value (standing rule 12) rather than a panel
   * guessing its own minimum from its content, which is exactly the "will
   * this overflow" question CSS answers for free once the box is tall
   * enough and never answers for one that is not. */
  minH?: PanelHeight;
  render(config: PanelConfig | undefined): JSX.Element | null;
  /** The panel's OWN action controls (e.g. Canvas's "Add a table…" /
   * "Remove a table…" selects) — N-090c moved these out of the panel's own
   * (now `chromeless`) header and into DeskGrid.tsx's title bar, right-
   * aligned, so they do not vanish along with the duplicate heading they
   * used to sit under. Returns null (nothing to add or remove right now) far
   * more often than not — DeskGrid renders nothing for that case, same as
   * omitting this field entirely (every type but `canvas`, today). */
  renderActions?(config: PanelConfig | undefined): JSX.Element | null;
  /** Synchronous "will this panel currently render something right now"
   * check. The panel's CELL always renders (N-090b: every panel is a
   * persistent window, like every other app's, whether or not it currently
   * has something to say) — this only decides what goes inside it: the
   * real content when true, a labelled "nothing to show" placeholder
   * (INV-9 — never an unlabelled blank) when false. Omitted for every type
   * whose component never returns null on its own (BirthdayAlerts always
   * shows an empty state; PinnedBoard-backed Canvas/People/Quick Docs
   * always show a card; Pay & Hours and the per-clinic boards are gated by
   * role/visible clinics, which DeskGrid has already filtered by the time
   * this runs). */
  hasContent?(config: PanelConfig | undefined): boolean;
  /** DeskGrid's placeholder text when `hasContent` says false — a type-
   * specific fact ("No SPC items due within 45 days.") rather than the
   * generic "Nothing to show right now." every panel used to share
   * regardless of what it was even for. Only meaningful alongside
   * `hasContent`; falls back to the generic line when omitted. */
  emptyLabel?: string;
}

const ALL_ROLES: Role[] = ['OWNER', 'DIRECTOR', 'MANAGER'];

export const PANEL_CATALOG: PanelCatalogEntry[] = [
  {
    type: 'clinicStrip',
    label: 'Clinics',
    // Owner, 2026-10-12: a one-clinic viewer (a manager) sees this as a
    // small box titled "Tasks" — not their clinic's name, which they know.
    labelFor: () => (visibleClinics().length === 1 ? 'Tasks' : 'Clinics'),
    // N-101 (owner, 2026-09-10): "i would like the CLinic tile to remove this:
    // One tile per clinic you can see, with this period's progress." — field
    // stays (its type is still `string`, and DeskAddMenu only renders the
    // description span when it is non-empty) so a future panel can use it.
    description: '',
    roles: ALL_ROLES,
    defaultSize: { w: 12, h: 2 },
    // N-090c (owner, 2026-09-08): at the shipped default of ONE row unit
    // (120px) this strip's own tiles clipped against the panel below it —
    // "No tasks this period" ran under the next row rather than inside its
    // own tile. Two units is the shortest height that never clips a tile.
    // No longer width-locked (owner, 2026-10-12, desk templates: "allow me to
    // move boxes around") — the manager seed has it as a 4-wide Tasks box.
    minH: 2,
    render: () => <ClinicOverviewStrip />,
    hasContent: hasTasksOrWorkItems,
    emptyLabel: 'No tasks or work items for your clinics yet.',
  },
  {
    type: 'payRateAlerts',
    label: 'Pay Rate',
    description: 'Employees who crossed 1,000 service hours since their last raise.',
    // Owner and director only again (owner, 2026-10-12: managers "should not
    // be getting things like ... for raise ... this should only be me").
    roles: ['OWNER', 'DIRECTOR'],
    defaultSize: { w: 4, h: 2 },
    render: () => <RaiseAlerts chromeless />,
    hasContent: payRateAlertsHasContent,
    emptyLabel: 'No 1,000-hour raises due right now.',
  },
  {
    // Owner, 2026-09-10: "i think the Pay Rate table is no loner available
    // right? ... should be the Pay and Hours Table." The People tab's Pay &
    // Hours table, per clinic, on the desk — every role, scoped like the
    // other clinic boards (a manager: their clinic; owner/director: pick).
    type: 'payHoursTable',
    label: 'Pay & Hours',
    description: 'The Pay & Hours table for one clinic.',
    roles: ALL_ROLES,
    defaultSize: { w: 12, h: 3 },
    clinicConfigurable: true,
    allClinics: true,
    render: (config) => <PayHoursTableForClinicPanel config={config} chromeless />,
    hasContent: hasAnyVisibleClinic,
  },
  {
    type: 'newHires',
    label: 'New Hires',
    description: 'People imported from a register with no hire details on file yet.',
    // Owner and director (Candace added 2026-10-06) — the server's own gate.
    roles: ['OWNER', 'DIRECTOR'],
    defaultSize: { w: 4, h: 1 },
    render: () => <NeedsHireDetailsAlert chromeless />,
    hasContent: newHiresHasContent,
    emptyLabel: 'No one is missing hire details.',
  },
  {
    type: 'spcAlerts',
    permanent: true,
    label: 'SPC',
    description: 'Service point compliance due within the alert window.',
    roles: ALL_ROLES,
    defaultSize: { w: 4, h: 2 },
    render: () => <ComplianceAlerts chromeless />,
    hasContent: spcAlertsHasContent,
    emptyLabel: `No SPC items due within ${ALERT_DAYS} days.`,
  },
  {
    type: 'birthdays',
    label: 'Birthdays',
    description: 'Birthdays coming up in the next five clinic days.',
    roles: ALL_ROLES,
    defaultSize: { w: 4, h: 1 },
    render: () => <BirthdayAlerts chromeless />,
  },
  {
    type: 'needsAttention',
    permanent: true,
    label: 'Needs attention',
    description: 'Tasks that are overdue, sent back, or blocked; licenses expiring within 90 days; and, for owner/director, leases expiring within 12 months and insurance policies expiring within 60 days.',
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 2 },
    render: () => <NeedsAttentionPanel chromeless />,
    hasContent: needsAttentionHasContent,
    emptyLabel: 'Nothing flagged — every task is on track.',
  },
  {
    type: 'canvas',
    label: 'Pinned Table Links',
    description: 'Every table you have pinned, as a quick link.',
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 2 },
    render: () => <Canvas chromeless />,
  },
  {
    type: 'people',
    label: 'People',
    description: 'Individuals you have pinned to keep in front of you.',
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 2 },
    render: () => <DeskPeople chromeless />,
  },
  {
    type: 'quickDocs',
    label: 'Quick Docs',
    description: 'Document folders you have pinned.',
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 1 },
    render: () => <QuickDocs chromeless />,
  },
  {
    type: 'spcListForClinic',
    label: 'SPC list',
    description: 'The full service point compliance board for one clinic.',
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 2 },
    clinicConfigurable: true,
    render: (config) => <SpcListForClinicPanel config={config} chromeless />,
    hasContent: hasAnyVisibleClinic,
  },
  {
    type: 'bonusTrackerForClinic',
    label: 'Bonus Tracker',
    description: 'The Bonus Tracker board for one clinic.',
    // Every role since 2026-10-06: a manager gets their own clinic,
    // read-only — exactly Company → My Folder → Bonus Tracker (the server
    // scopes the read and decides `canWrite`).
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 2 },
    clinicConfigurable: true,
    render: (config) => <BonusTrackerForClinicPanel config={config} chromeless />,
    hasContent: hasAnyVisibleClinic,
  },
  {
    type: 'depositReconForClinic',
    label: 'Deposit Recon',
    description: 'The deposit reconciliation grid for one clinic.',
    // Every role since 2026-10-06: a manager gets their own clinic with the
    // same rights as Money → Deposits (closed-batch delete stays
    // owner/director, DepositReconGrid.tsx canDeleteClosedBatch).
    roles: ALL_ROLES,
    defaultSize: { w: 8, h: 3 },
    clinicConfigurable: true,
    render: (config) => <DepositReconForClinicPanel config={config} chromeless />,
    hasContent: hasAnyVisibleClinic,
  },
  {
    // N-094 ("forward and file"): Tomer forwards email to file@nvmanager.com;
    // a backend job files it as a license, stores it whole as a
    // Communication, or drops it into Intake (Company → Intake; off the Desk since 2026-10-12) for a human to decide.
    type: 'communications',
    permanent: true,
    label: 'Communications',
    description: 'Emails filed by forward-to-file, newest first.',
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 2 },
    render: () => <CommunicationsPanel chromeless />,
    hasContent: communicationsHasContent,
    emptyLabel: 'No communications yet.',
  },
  {
    // Owner, 2026-09-10 and "build it" 2026-09-18: the signed-in person's
    // own recent inbox. On every default Desk since 2026-10-06.
    type: 'myMail',
    permanent: true,
    label: 'My Mail',
    description: 'Your own recent inbox.',
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 2 },
    // Opened to everyone (owner, 2026-10-06: "let's get the mail etc") —
    // each person sees their own inbox.
    render: () => <MyMailPanel chromeless />,
    hasContent: myGooglePanelHasContent,
    emptyLabel: 'Hidden while you are viewing as someone else.',
  },
  {
    type: 'myCalendar',
    label: 'My Calendar',
    description: 'Your own calendar, the next 7 days.',
    roles: ALL_ROLES,
    defaultSize: { w: 6, h: 2 },
    // Opened to everyone (owner, 2026-10-06) — each person's own calendar.
    render: () => <MyCalendarPanel chromeless />,
    hasContent: myGooglePanelHasContent,
    emptyLabel: 'Hidden while you are viewing as someone else.',
  },
  {
    // Owner ask, 2026-09-25: New today / To review / Quiet 7+ days / Offers
    // out, plus the LMT roster headcount — GET /api/v1/recruiting/summary
    // (src/recruiting.js), the same endpoint and resource-cache key
    // RecruitingBoard.tsx (People → Recruiting tab) already uses. Owner
    // preview like My Mail / My Calendar above, until Tomer approves it.
    type: 'recruiting',
    permanent: true,
    label: 'nvRecruiter',
    description: 'New Indeed applicants waiting to be emailed, plus new candidates, review queue, and offers out, by clinic.',
    roles: ALL_ROLES,
    defaultSize: { w: 4, h: 2 },
    clinicConfigurable: true,
    allClinics: true,
    render: (config) => (
      recruitingPreview() ? <RecruitingDeskPanel config={config} chromeless /> : <ComingSoonPanel what="nvRecruiter" />
    ),
    hasContent: recruitingHasContent,
    emptyLabel: 'Nothing waiting in recruiting',
  },
];

/** What everyone but the owner sees for a feature still in owner preview
 * (lib/ownerPreview.ts). */
function ComingSoonPanel({ what }: { what: string }): JSX.Element {
  return (
    <div class="coming-soon" data-testid="desk-coming-soon">
      <strong>Coming soon</strong>
      <span class="muted">A quick view of {what}, right here on your Desk.</span>
    </div>
  );
}

export function catalogEntry(type: string): PanelCatalogEntry | undefined {
  return PANEL_CATALOG.find((e) => e.type === type);
}

/** The panel types a given role may see or add, in catalog order. */
export function catalogForRole(role: Role | null | undefined): PanelCatalogEntry[] {
  if (!role) return [];
  return PANEL_CATALOG.filter((e) => e.roles.includes(role));
}

/** A panel saved SHORTER than its type's current `minH` (N-090c added that
 * field after some layouts — including the shipped default, before this
 * fix — had already saved a `clinicStrip` at the old one-unit height) is
 * bumped up to it here, pushing anything it now overlaps out of the way
 * with the same `resolveOverlaps` a live resize uses. Read-only: the fix
 * lands in whatever a caller does next with this list (render it, pack a
 * new panel around it) — it is not written back to the saved layout on its
 * own, so this runs identically every time the layout is read, at no extra
 * cost when nothing actually needs raising (the common case, checked before
 * ever touching `resolveOverlaps`). */
function withMinHeights(panels: DeskPanelInstance[]): DeskPanelInstance[] {
  let changed = false;
  const floored = panels.map((p) => {
    const minH = catalogEntry(p.type)?.minH;
    if (minH && p.h < minH) {
      changed = true;
      return { ...p, h: minH };
    }
    return p;
  });
  return changed ? resolveOverlaps(floored) : panels;
}

/** The panels of a saved layout THIS role may actually see, in the saved
 * order. Shared by DeskGrid.tsx (what to render) and DeskAddMenu.tsx (what
 * "Add panel" packs new panels around) so the two never compute two
 * different answers to "what's on the canvas right now" — one register, per
 * standing rule 12.
 *
 * Defense in depth, unchanged from before this was split out: a saved row
 * is already server-validated against this same role at write time, but a
 * role can change (a promotion, a demotion) between when a layout was saved
 * and when it is next read — never render, or pack around, a panel this
 * session's role may not see. */
export function visiblePanelsFor(layout: DeskLayout, role: Role): DeskPanelInstance[] {
  const forRole = layout.panels.filter((p) => {
    const entry = catalogEntry(p.type);
    return Boolean(entry && entry.roles.includes(role));
  });
  // Owner, 2026-10-12: the five always-on panels are always present
  // (fixedBlock.ts withPermanentPanels) — anywhere on the Desk. Applied here,
  // the one place DeskGrid and DeskAddMenu read the canvas from, so the
  // screen (and the next save) match what the server stores.
  return withPermanentPanels(withMinHeights(forRole), (type) => Boolean(catalogEntry(type)?.roles.includes(role)), role);
}

/**
 * Tomer's own Desk as `role` sees it (owner, 2026-10-12: "I want to edit the
 * layout for myself and that carries to everyone"): the same panels in the
 * same places, minus any panel the role may not see. For a MANAGER (one
 * clinic) a clinic panel type appears once — every copy would show the same
 * clinic, theirs — and no panel ever appears twice. When anything was left
 * out the rest float up into the hole (gridPack.ts floatUp); then the
 * always-on panels are kept present. Pure and idempotent. Server twin:
 * src/desk.js ownerDeskFor — the server already sends a manager or director
 * this; the client runs it for view-as and for "Tomer never saved one".
 */
const MANAGER_TASKS_WIDTH = 4;

export function ownerDeskFor(layout: DeskLayout, role: Role): DeskLayout {
  const kept: DeskPanelInstance[] = [];
  const seen = new Set<string>();
  let dropped = false;
  for (const p of layout.panels) {
    const entry = catalogEntry(p.type);
    if (!entry || !entry.roles.includes(role)) { dropped = true; continue; }
    const key = role === 'MANAGER' && entry.clinicConfigurable ? p.type : panelKey(p);
    if (seen.has(key)) { dropped = true; continue; }
    seen.add(key);
    // One clinic: the Tasks box is a quarter-width box and what was below it
    // moves up beside it (owner, 2026-10-12). Mirrors src/desk.js.
    if (role === 'MANAGER' && p.type === 'clinicStrip' && p.w > MANAGER_TASKS_WIDTH) {
      kept.push({ ...p, w: MANAGER_TASKS_WIDTH });
      dropped = true;
      continue;
    }
    kept.push({ ...p });
  }
  const mayShow = (type: string) => Boolean(catalogEntry(type)?.roles.includes(role));
  return { version: 1, panels: withPermanentPanels(dropped ? floatUp(kept) : kept, mayShow, role) };
}

/** What makes two panels "the same panel" (owner, 2026-10-12: "if a table
 * is already in the desk it shouldn't let you re-add it"): the type, plus
 * the clinic for clinic-configurable types — a second Pay & Hours table is
 * fine for a different clinic, never for the same one. Mirrors
 * src/desk.js's panelKey exactly. */
export function panelKey(panel: Pick<DeskPanelInstance, 'type' | 'config'>): string {
  return `${panel.type}|${panel.config?.clinic ?? ''}`;
}

/** The clinic choices a clinic-configurable type offers ("all" first for
 * types that can sum every clinic), given the clinics the Desk's person can
 * see. */
export function clinicChoicesFor(entry: PanelCatalogEntry, clinics: readonly string[]): string[] {
  return [...(entry.allClinics ? ['all'] : []), ...clinics];
}

/** The config a NEW panel of this type would get, or null when every
 * allowed copy is already on the Desk — "+ Add panel" shows the type
 * disabled ("Already on your Desk") instead of adding a duplicate. A
 * non-clinic type gets `{}` (no config) when it is not on the Desk yet. */
export function nextAddableConfig(
  entry: PanelCatalogEntry,
  panels: readonly DeskPanelInstance[],
  clinics: readonly string[],
): PanelConfig | null {
  const used = new Set(panels.map(panelKey));
  if (!entry.clinicConfigurable) {
    return used.has(panelKey({ type: entry.type })) ? null : {};
  }
  const choices = clinicChoicesFor(entry, clinics);
  const free = choices.find((clinic) => !used.has(panelKey({ type: entry.type, config: { clinic } })));
  if (free !== undefined) return { clinic: free };
  return choices.length ? null : (used.has(panelKey({ type: entry.type, config: { clinic: 'all' } })) ? null : { clinic: 'all' });
}

/** The permanent panel types (owner, 2026-10-12), in catalog order. Mirrors
 * src/desk.js PERMANENT_PANEL_TYPES. */
export const PERMANENT_PANEL_TYPES: PanelType[] = PANEL_CATALOG
  .filter((e) => e.permanent).map((e) => e.type);
